import { cn } from "@/lib/utils";
import { ArrowUp, MessageSquareIcon, PanelRight } from "lucide-react";
import Image from "next/image";
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { NoxusIcon } from "./UI/NoxusIcon";
import { getSpotFileUrl } from "@/utils/generic";
import { extractImageUrl, isValidSvgString } from "@/utils/generic";
import DOMPurify from "isomorphic-dompurify";

const PREVIEW_H = 300;
const PREVIEW_PANEL_H = 190;
const PREVIEW_BUBBLE = 48;
const PREVIEW_GAP = 8;
const PREVIEW_MARGIN = 10;
// The frame draws the panel at ~half its real width; gaps follow it.
const PREVIEW_GAP_SCALE = 0.5;
// Mirrors DEFAULT_WIDGET_DISTANCE / MAX_WIDGET_DISTANCE in io/triggers/noxus.py.
const DEFAULT_WIDGET_DISTANCE = 20;
const MAX_WIDGET_DISTANCE = 100;

type WidgetPreviewProps = {
  logoSvg?: string;
  icon?: string;
  isSmall?: boolean;
  customization?: Record<string, any>;
};

export function WidgetPreview({
  isSmall,
  icon,
  customization,
}: WidgetPreviewProps) {
  const appearTime = 0.75;
  const timeBetween = 0.5;
  const LOOP_MS = 20000;

  const [cycleKey, setCycleKey] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setCycleKey((k) => k + 1), LOOP_MS);
    return () => clearInterval(id);
  }, []);

  const fadeUp = (delay: number) => ({
    initial: { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: appearTime, delay },
  });

  const { primary, accent } = useMemo(() => {
    if (!customization?.apply_custom_theme || !customization?.main_color) {
      return {
        primary: "var(--primary)",
        accent: "var(--accent)",
      };
    }

    const colors = customization.main_color as {
      primary: string;
      accent: string;
    };

    // Default theme or CSS variable defaults — no color overrides
    if (
      (colors.primary === "#1E1E1E" && colors.accent === "#F0F5FA") ||
      colors.primary.startsWith("var(") ||
      colors.accent.startsWith("var(")
    ) {
      return {
        primary: "var(--primary)",
        accent: "var(--accent)",
      };
    }

    return colors;
  }, [customization]);

  const anchorVertical =
    customization?.position_vertical === "top" ? "top" : "bottom";
  const anchorHorizontal =
    customization?.position_horizontal === "left" ? "left" : "right";

  const gap = (key: string) => {
    const raw = Number(customization?.[key]);
    const px = Number.isFinite(raw)
      ? Math.min(Math.max(raw, 0), MAX_WIDGET_DISTANCE)
      : DEFAULT_WIDGET_DISTANCE;
    return px * PREVIEW_GAP_SCALE;
  };
  const gapLeft = gap("position_horizontal_distance");
  const gapTop = gap("position_vertical_distance");

  // Mirrors panelPositionFor in embed.js: above, else below, else alongside.
  const bubbleTop =
    anchorVertical === "top" ? gapTop : PREVIEW_H - PREVIEW_BUBBLE - gapTop;
  const fitsAbove = bubbleTop - PREVIEW_GAP - PREVIEW_PANEL_H >= PREVIEW_MARGIN;
  const fitsBelow =
    bubbleTop + PREVIEW_BUBBLE + PREVIEW_GAP + PREVIEW_PANEL_H <=
    PREVIEW_H - PREVIEW_MARGIN;
  const nearBottom = bubbleTop + PREVIEW_BUBBLE / 2 > PREVIEW_H / 2;
  const panelTop = fitsAbove
    ? bubbleTop - PREVIEW_GAP - PREVIEW_PANEL_H
    : fitsBelow
      ? bubbleTop + PREVIEW_BUBBLE + PREVIEW_GAP
      : Math.min(
          Math.max(
            nearBottom
              ? bubbleTop + PREVIEW_BUBBLE - PREVIEW_PANEL_H
              : bubbleTop,
            PREVIEW_MARGIN
          ),
          PREVIEW_H - PREVIEW_PANEL_H - PREVIEW_MARGIN
        );

  const titleLogo = extractImageUrl(customization?.title_logo);

  const svg = customization?.logo_svg;

  const isValidSvg = isValidSvgString(svg);
  // isValidSvgString only proves it parses as SVG; onload and friends survive it.
  const safeSvg = isValidSvg
    ? DOMPurify.sanitize(svg ?? "", {
        USE_PROFILES: { svg: true, svgFilters: true },
      })
    : "";

  if (isSmall) {
    return (
      <div className="flex flex-col gap-2">
        <div
          className={cn(
            "flex flex-col rounded-lg shadow-sm p-4 w-full gap-6 h-12 mt-1 self-end bg-[#FCFCFC]"
          )}
          style={{ backgroundColor: accent }}
        ></div>
        <div
          className="flex items-center justify-center w-12 h-12 rounded-full p-3 self-end"
          style={{ backgroundColor: primary }}
        >
          {isValidSvg ? (
            <div dangerouslySetInnerHTML={{ __html: safeSvg }} />
          ) : (
            <Image
              src="https://storage.googleapis.com/image-storage-spot-manual/app/sidenav/noxus-o.svg"
              alt={"Noxus logo"}
              style={{
                height: "100%",
                width: "auto",
              }}
              width={16}
              height={8}
              className="brightness-0 invert"
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col border border-border rounded-md bg-background overflow-hidden">
      <div className="relative w-full flex justify-center items-center py-2 px-4 border-b border-border bg-gray-100">
        {" "}
        <div className="absolute left-4 flex gap-2 items-center mr-auto">
          {" "}
          <div className="h-3 w-3 rounded-full bg-red-500" />{" "}
          <div className="h-3 w-3 rounded-full bg-yellow-500" />{" "}
          <div className="h-3 w-3 rounded-full bg-green-500" />{" "}
        </div>{" "}
        <p className="text-sm text-muted-foreground/80">www.website.com</p>{" "}
      </div>

      <div className="p-4">
        <div className="relative h-[300px] overflow-hidden">
          <div
            className="absolute"
            style={{
              left:
                anchorHorizontal === "left"
                  ? `${gapLeft}px`
                  : `calc(100% - ${PREVIEW_BUBBLE + gapLeft}px)`,
              top: `${bubbleTop}px`,
            }}
          >
            {!isSmall && (
              <>
                {/* Chat */}
                {/* Main content */}
                <div
                  className={cn(
                    "absolute w-[240px] h-[190px] flex flex-col rounded-xl p-6 gap-6 shadow-md pb-4 bg-[#fcfcfc]",
                    fitsAbove || fitsBelow
                      ? anchorHorizontal === "right"
                        ? "right-0"
                        : "left-0"
                      : anchorHorizontal === "right"
                        ? "right-full mr-3"
                        : "left-full ml-3"
                  )}
                  style={{ top: `${panelTop - bubbleTop}px` }}
                  key={cycleKey}
                >
                  {/* Top bubble area */}
                  <motion.div
                    {...(isSmall ? {} : fadeUp(0))}
                    className="flex flex-col rounded-lg px-3 py-2 w-5/7 self-end h-10"
                    style={{ backgroundColor: accent }}
                  />

                  {/* Message 1 */}
                  <motion.div
                    {...fadeUp(timeBetween)}
                    className="flex items-start gap-4"
                  >
                    {!isSmall && (
                      <div className="relative">
                        <div className="h-6 w-6 rounded-md border border-border flex items-center justify-center overflow-hidden">
                          {titleLogo ? (
                            <img
                              src={titleLogo}
                              alt="Header logo"
                              className="h-full w-full object-contain"
                            />
                          ) : (
                            <NoxusIcon icon={icon || "workflow"} size="sm" />
                          )}
                        </div>
                      </div>
                    )}
                    <div className="flex-1 space-y-1">
                      <div className="h-3 w-3/4 rounded-full bg-border" />
                      <div className="h-3 w-1/2 rounded-full bg-border" />
                    </div>
                  </motion.div>

                  {/* Powered By */}
                  <div className="flex flex-col mt-2">
                    <div className="flex justify-end border border-border rounded-md p-1">
                      <ArrowUp
                        color="var(--color-gray-200)"
                        size={16}
                        strokeWidth={3}
                      />
                    </div>
                    {/* <div className="flex items-center justify-center gap-1.5 mt-2">
                  <p className="text-muted-foreground text-xs font-regular">
                    Powered by
                  </p>
                  <div className="h-full transition-all duration-250 ease-in-out max-w-16">
                    <Image
                      src="https://storage.googleapis.com/image-storage-spot-manual/app/sidenav/noxus.svg"
                      alt={"Noxus text logo"}
                      style={{
                        height: "0.625rem",
                        width: "auto",
                      }}
                      width={10}
                      height={6}
                      className="dark:invert dark:brightness-0"
                    />
                  </div>
                </div> */}
                  </div>
                </div>
              </>
            )}

            {/* Bubble */}
            <div
              className="w-12 h-12 rounded-full p-3 flex items-center justify-center overflow-hidden"
              style={{ backgroundColor: primary }}
            >
              {isValidSvg ? (
                <div dangerouslySetInnerHTML={{ __html: safeSvg }} />
              ) : (
                <MessageSquareIcon size={20} strokeWidth={1.33} color="#fff" />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WidgetPreview;
